/**
 * Catering card order.
 *
 * The page ships four fixed cards — Starters, Biryanis, Curries, Breads — and
 * any number the owner adds in the editor. They used to be two different
 * things: the fixed four were hardcoded JSX in a fixed order, and only the
 * added ones could move, and only below the fixed four.
 *
 * These helpers present all of them as one list so reordering treats them
 * alike. `data.cardOrder` records the running order as card keys; a document
 * published before this existed has no `cardOrder`, which reads as the order
 * the page always had.
 *
 * Pure — no React, no Firestore.
 */

/** The cards that are part of the page itself. Renameable: no. Movable: yes. */
export const BUILTIN_CARDS = [
  { key: 'starters', kind: 'trays' },
  { key: 'biryanis', kind: 'trays' },
  { key: 'curries', kind: 'trays' },
  { key: 'breads', kind: 'table', title: 'Breads', icon: 'Bread', cols: ['Item', 'Min.', 'Price'] },
];

const BUILTIN_KEYS = BUILTIN_CARDS.map((c) => c.key);

/**
 * Every card in the first menu band, in render order.
 *
 * @returns entries of `{ key, kind, builtin, section, index? }` where
 *          `section` is the card's data and `index` its position in
 *          `customSections` (custom cards only).
 */
export function orderedCards(data) {
  if (!data) return [];

  const custom = (data.customSections || []).map((section, index) => ({
    key: section.id || `custom-${index}`,
    kind: section.kind === 'table' ? 'table' : 'trays',
    builtin: false,
    section,
    index,
  }));

  const builtin = BUILTIN_CARDS
    .filter((c) => data[c.key])
    .map((c) => ({
      key: c.key,
      kind: c.kind,
      builtin: true,
      section: c.kind === 'table'
        ? { title: c.title, icon: c.icon, cols: c.cols, items: data[c.key] }
        : data[c.key],
    }));

  const all = [...builtin, ...custom];
  const order = data.cardOrder;
  if (!Array.isArray(order)) return all;

  // Anything the saved order doesn't mention — a card added since, or one this
  // build introduced — keeps its natural place at the end rather than vanishing.
  const byKey = new Map(all.map((e) => [e.key, e]));
  const listed = order.map((k) => byKey.get(k)).filter(Boolean);
  const seen = new Set(listed.map((e) => e.key));
  return [...listed, ...all.filter((e) => !seen.has(e.key))];
}

/** Move the card at position `from` to position `to`. */
export function moveCard(data, from, to) {
  const list = orderedCards(data);
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return data;
  const next = [...list];
  next.splice(to, 0, ...next.splice(from, 1));
  return { ...data, cardOrder: next.map((e) => e.key) };
}

/** Write a card's edits back to wherever that card actually lives. */
export function updateCard(data, entry, section) {
  if (!entry.builtin) {
    return {
      ...data,
      customSections: (data.customSections || [])
        .map((s, i) => (i === entry.index ? section : s)),
    };
  }
  // A built-in table card stores a bare item array, not a section object.
  return { ...data, [entry.key]: entry.kind === 'table' ? section.items : section };
}

/** True for keys the page owns and the editor must not rename or delete. */
export const isBuiltinCard = (key) => BUILTIN_KEYS.includes(key);
